import { useEffect, useRef, useState, type FormEvent } from 'react';
import { trackPageView, Analytics } from '../utils/analytics';
import { sendContactForm, type ContactFormData } from '../api/contact';
import SyncAnimation from '../components/SyncAnimation';
import '../styles/audit.css';
import '../styles/reports.css';
import '../styles/mainpage.css';

/* ============================================
   INLINE SVG ICONS
   ============================================ */

const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconArrow = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="M12 5l7 7-7 7" />
  </svg>
);

const IconShield = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

/* ============================================
   REFS CAROUSEL — auto-discovery
   ============================================ */

const logoModules = import.meta.glob('../assets/audit/logos/client-*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const logos: string[] = Object.entries(logoModules)
  .map(([path, url]) => {
    const m = path.match(/client-(\d+)\.png$/);
    return { n: m ? parseInt(m[1], 10) : 0, url };
  })
  .sort((a, b) => a.n - b.n)
  .map(x => x.url);

const MARQUEE_SPEED = 40;

function RefsCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isHovered = useRef(false);
  const isDragRef = useRef(false);
  const dragState = useRef<{ startX: number; scrollLeft: number; moved: boolean } | null>(null);

  const COPIES = 3;
  const displayLogos = Array.from({ length: COPIES }, () => logos).flat();

  useEffect(() => {
    const el = trackRef.current;
    if (!el || logos.length === 0) return;

    const initScroll = () => {
      const oneCopyWidth = el.scrollWidth / COPIES;
      el.scrollLeft = oneCopyWidth;
    };
    initScroll();
    const onResize = () => initScroll();
    window.addEventListener('resize', onResize);

    let rafId = 0;
    let lastTime = performance.now();

    const loop = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!isHovered.current && !isDragRef.current) {
        el.scrollLeft += MARQUEE_SPEED * dt;
      }

      const oneCopyWidth = el.scrollWidth / COPIES;
      if (el.scrollLeft >= oneCopyWidth * 2) {
        el.scrollLeft -= oneCopyWidth;
      } else if (el.scrollLeft < oneCopyWidth * 0.5 && dragState.current === null) {
        el.scrollLeft += oneCopyWidth;
      }

      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { startX: e.clientX, scrollLeft: el.scrollLeft, moved: false };
    isDragRef.current = true;
    setIsDragging(true);
    el.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el || !dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 4) dragState.current.moved = true;
    el.scrollLeft = dragState.current.scrollLeft - dx;
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (el) el.releasePointerCapture?.(e.pointerId);
    isDragRef.current = false;
    setIsDragging(false);
    setTimeout(() => { dragState.current = null; }, 50);
  };

  if (logos.length === 0) return null;

  return (
    <div className="audit-refs-carousel">
      <div
        ref={trackRef}
        className={`audit-refs-track${isDragging ? ' audit-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={() => { isHovered.current = true; }}
        onMouseLeave={() => { isHovered.current = false; }}
      >
        {displayLogos.map((url, i) => (
          <div key={i} className="audit-ref-tile">
            <img src={url} alt="Reference klienta" loading="lazy" draggable={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================
   NAV
   ============================================ */

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav className={`audit-nav${scrolled ? ' scrolled' : ''}`}>
      <div className="audit-nav-inner">
        <a href="/" className="audit-nav-logo">
          <img src="/logo_light2.png" alt="SyncroX" />
        </a>
        <a href="#form" className="audit-nav-cta">Domluvit ukázku</a>
      </div>
    </nav>
  );
}

/* ============================================
   CONTACT FORM
   ============================================ */

function MainForm() {
  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: 'platforma-poptavka', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Mám zájem o SyncroX platformu — chci ukázku.',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Hotovo. Ozveme se do 24 hodin.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: 'platforma-poptavka', message: '' });
        Analytics.contactFormSubmit('platforma-poptavka');
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Chyba připojení. Napište nám na obchod@syncrox.cz.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="audit-form" onSubmit={handleSubmit}>
      <h3>Domluvit ukázku platformy</h3>
      <div className="audit-form-sub">30 minut online · bez závazku</div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>Jméno *</label>
          <input className="audit-form-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jan Novák" />
        </div>
        <div className="audit-form-group">
          <label>Firma *</label>
          <input className="audit-form-input" required value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Firma s.r.o." />
        </div>
      </div>
      <div className="audit-form-row">
        <div className="audit-form-group">
          <label>E-mail *</label>
          <input className="audit-form-input" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jan@firma.cz" />
        </div>
        <div className="audit-form-group">
          <label>Telefon</label>
          <input className="audit-form-input" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+420..." />
        </div>
      </div>
      <div className="audit-form-group">
        <label>Co u vás nejvíc pálí? (nepovinné)</label>
        <textarea className="audit-form-textarea" rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Např. přepisujeme výpisy ručně, platby se nepárují, nemáme přehled..." />
      </div>
      <button type="submit" className="audit-form-submit" disabled={submitting}>
        {submitting ? 'Odesílám…' : 'Domluvit ukázku zdarma'}
      </button>
      {status && <div className={`audit-form-status ${status.type}`}>{status.msg}</div>}
      <p className="audit-form-fine">Ozveme se do 24 hodin. Obvykle dřív.</p>
    </form>
  );
}

/* ============================================
   EXIT INTENT POPUP
   ============================================ */

function ExitIntent() {
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);

  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: 'exit-audit-zdarma', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    if (sessionStorage.getItem('rep-exit-shown') === '1') {
      shownRef.current = true;
      return;
    }
    const onMouseLeave = (e: MouseEvent) => {
      if (shownRef.current) return;
      if (e.clientY <= 0) {
        shownRef.current = true;
        sessionStorage.setItem('rep-exit-shown', '1');
        setOpen(true);
      }
    };
    const timer = setTimeout(() => {
      document.documentElement.addEventListener('mouseleave', onMouseLeave);
    }, 3000);
    const backupTimer = setTimeout(() => {
      if (!shownRef.current) {
        shownRef.current = true;
        sessionStorage.setItem('rep-exit-shown', '1');
        setOpen(true);
      }
    }, 60_000);
    return () => {
      clearTimeout(timer);
      clearTimeout(backupTimer);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Chci audit zdarma (exit popup z hlavní stránky).',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Hotovo! Ozveme se do 24 hodin.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: 'exit-audit-zdarma', message: '' });
        Analytics.contactFormSubmit('exit-audit-zdarma');
        setTimeout(() => setOpen(false), 1800);
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Napište nám na obchod@syncrox.cz.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`rep-exit-overlay${open ? ' rep-exit-open' : ''}`} onClick={() => setOpen(false)}>
      <div className="rep-exit-card" onClick={e => e.stopPropagation()}>
        <button type="button" className="rep-exit-close" onClick={() => setOpen(false)} aria-label="Zavřít">×</button>
        <div className="rep-exit-top">
          <div className="rep-exit-label">Počkejte chvilku</div>
          <h3>Než odejdete — zjistěte, kolik vás ročně stojí ruční práce.</h3>
          <p>60 minut s expertem · PDF plán s konkrétními čísly pro vaši firmu · mobilní aplikace zdarma</p>
        </div>
        <div className="rep-exit-body">
          <div className="rep-exit-perks">
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>Analýza vašich systémů</strong> — co se dá zdigitalizovat u vás konkrétně</div>
            </div>
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>Vyčíslené úspory v Kč</strong> — ne průměry, vaše čísla</div>
            </div>
            <div className="rep-exit-perk">
              <span className="rep-exit-perk-check"><IconCheck size={13} /></span>
              <div><strong>PDF plán zůstává váš</strong> — i když dál nepokračujete</div>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="rep-exit-form-row">
              <input className="rep-exit-input" required placeholder="Jméno" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <input className="rep-exit-input" required placeholder="Firma" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />
            </div>
            <input className="rep-exit-input" required type="email" placeholder="E-mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <button type="submit" className="rep-exit-submit" disabled={submitting}>
              {submitting ? 'Odesílám…' : 'Chci audit zdarma'}
            </button>
            {status && <div className={`rep-exit-status ${status.type}`}>{status.msg}</div>}
            <p className="rep-exit-fine">Bez spamu. Bez prodejních tlaků. Ozveme se jednou — do 24 hodin.</p>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============================================
   FAQ
   ============================================ */

function Faq() {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: 'Co přesně SyncroX dělá?', a: 'Propojujeme vaše systémy — e-shop, účetnictví, platební brány, rezervace, sklady, výrobní linky — do jednoho toku. K tomu stavíme manažerské reporty, mobilní aplikaci a poskytujeme průběžnou údržbu. Vše v jednom měsíčním tarifu.' },
    { q: 'Kolik to stojí?', a: 'Jeden tarif zahrnuje vše: napojení účetního systému, všechny integrace, manažerské reporty, mobilní aplikaci, setup, podporu i průběžné úpravy. Cenu se odvíjí od velikosti firmy a počtu systémů. Přesné číslo dostanete na 30min konzultaci — bez kličkování.' },
    { q: 'Kolik integrací máte?', a: 'Přes 20 standardních v nabídce: Pohoda, Money S3, Helios, Flexi, Abra, Shoptet, WooCommerce, Shopify, Upgates, GoPay, Comgate, Stripe, PayPal, Mews, Previo, Reenio, Zásilkovna, PPL a další. Pokud váš systém v seznamu není, doděláme ho — zatím se nám ještě žádný nevyhnul.' },
    { q: 'Jak dlouho trvá rozjezd?', a: 'Typicky 5–10 pracovních dní od dodání přístupů. Jednodušší případy spustíme i za 48 hodin. Během rozjezdu vás vedeme krok za krokem.' },
    { q: 'Máme specifické procesy — půjde to?', a: 'Každá firma je jiná. Reporty i integrace nastavujeme přímo na vaše procesy, ne do šablony. Pokud máte něco extra neobvyklé, to je přesně case, kde se hodí digitalizační audit — řekneme vám rovnou, co jde a co ne.' },
    { q: 'Musíme se k něčemu zavazovat?', a: 'Ne. Jedeme měsíc po měsíci. Pokud vám řešení nevyhovuje, vypnete. Žádné výpovědní doby, žádné skryté smlouvy, žádné sankce.' },
    { q: 'Je to bezpečné?', a: 'Data jsou v EU cloudu, denně zálohujeme, komunikace je šifrovaná. Přístup kontroluje admin vaší firmy — ne každý uživatel vidí všechno. GDPR compliant, včetně pravidel pro zpracování osobních údajů.' },
  ];
  return (
    <div className="audit-faq">
      {faqs.map((f, i) => (
        <div key={i} className={`audit-faq-item${open === i ? ' open' : ''}`}>
          <button type="button" className="audit-faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
            <span>{f.q}</span>
            <span className="audit-faq-toggle">+</span>
          </button>
          <div className="audit-faq-a">{f.a}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================
   INTEGRATION LOGOS
   ============================================ */

const integrationLogos = [
  { name: 'Pohoda', src: '/integrations/pohoda.png' },
  { name: 'Money S3', src: '/integrations/moneyS3.webp' },
  { name: 'Abra Flexibee', src: '/integrations/abra flexibee.jpg' },
  { name: 'Shoptet', src: '/integrations/eshop-rychle.png' },
  { name: 'GoPay', src: '/integrations/pays.png' },
  { name: 'Comgate', src: '/integrations/comgate.jpg' },
  { name: 'PayPal', src: '/integrations/paypal.png' },
  { name: 'Mews', src: '/integrations/mews.webp' },
  { name: 'Previo', src: '/integrations/previo.png' },
  { name: 'Reenio', src: '/integrations/reenio.png' },
  { name: 'Zásilkovna', src: '/integrations/Zasilkovna.png' },
  { name: 'PPL', src: '/integrations/ppl.png' },
];

/* ============================================
   MAIN PAGE
   ============================================ */

export default function LandingPage() {
  useEffect(() => {
    trackPageView('/', 'SyncroX — Platforma pro digitalizaci firem');
    window.scrollTo(0, 0);

    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const a = t.closest('a');
      if (!a || !a.hash || a.origin !== window.location.origin) return;
      const el = document.querySelector(a.hash);
      if (!el) return;
      e.preventDefault();
      const y = (el as HTMLElement).getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  return (
    <div className="audit-page mainpage">
      <Nav />

      {/* ========== HERO ========== */}
      <section className="audit-hero">
        <div className="audit-container">
          <div className="audit-hero-grid">
            <div>
              <div className="rep-hero-trustrow">
                <span className="rep-trust-stars">★★★★★</span>
                <span>4,9 / 5</span>
                <span style={{ color: 'var(--a-muted)' }}>·</span>
                <span>50+ firem</span>
              </div>
              <div className="audit-hero-eyebrow">
                Platforma pro digitalizaci firem
              </div>
              <h1>
                Vaše systémy <span className="audit-underline">propojené</span>. Vaše data na mobilu. Vaše firma zautomatizovaná.
              </h1>
              <p className="audit-hero-sub">
                SyncroX propojí váš e-shop, platební brány, účetnictví, sklady i výrobu do <strong>jednoho toku</strong>. K tomu dodáme manažerské reporty, mobilní aplikaci a průběžnou údržbu — všechno v jedné ceně.
              </p>
              <div className="audit-hero-ctas">
                <a href="#form" className="audit-btn-primary">
                  Domluvit ukázku
                  <IconArrow />
                </a>
                <a href="#pillars" className="audit-btn-secondary">Co platforma umí</a>
              </div>
              <div className="audit-hero-trust">
                <div className="audit-trust-item">
                  <div className="audit-trust-n">20+</div>
                  <div className="audit-trust-l">standardních<br/>integrací</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">5–10</div>
                  <div className="audit-trust-l">dní do plného<br/>provozu</div>
                </div>
                <div className="audit-trust-item">
                  <div className="audit-trust-n">Vše v ceně</div>
                  <div className="audit-trust-l">integrace, reporty<br/>i aplikace</div>
                </div>
              </div>
            </div>

            {/* Hero visual — SyncAnimation */}
            <div>
              <SyncAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* ========== BIG NUMBER BAND ========== */}
      <section className="audit-bignum">
        <div className="audit-container">
          <div className="audit-bignum-num">60+ hodin</div>
          <div className="audit-bignum-label">
            <strong>Měsíčně ušetří průměrný klient</strong> na ručním přepisování<br/>
            dat, párování plateb a sestavování manažerských výkazů.
          </div>
        </div>
      </section>

      {/* ========== CLIENT LOGOS — MARQUEE ========== */}
      <section className="audit-refs">
        <div className="audit-container">
          <div className="audit-refs-head">
            <div className="audit-refs-title">Důvěřuje nám</div>
            <div className="audit-refs-sub">Klienti, kterým už data tečou samy</div>
          </div>
          <RefsCarousel />
          <div className="audit-refs-note">
            E-shopy, výroba, hotelnictví, služby i účetní firmy. <strong>Všichni přestali přepisovat.</strong>
          </div>
        </div>
      </section>

      {/* ========== PROBLEM ========== */}
      <section className="audit-section" id="problem">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Poznáte se?</span>
            <h2>Vaše systémy jsou ostrovy.<br/>Mezi nimi lidé jako poštovní holubi.</h2>
            <p>Pokud kývete u 3 a víc bodů, SyncroX je pro vás.</p>
          </div>
          <div className="audit-pains">
            {[
              'Objednávka přijde do e-shopu, ale fakturu vystavujete ručně. Nebo ji někdo přepisuje jinam.',
              'Platby z brány se nepárují s fakturami automaticky — účetní si s tím trápí hodiny.',
              'Bankovní výpisy stahujete ručně a pak je ručně importujete do účetnictví.',
              'Reálné číslo o firmě znáte až po uzávěrce. Mezitím rozhodujete podle pocitu.',
              'Máte 4+ systémy, které spolu nemluví. Dát data dohromady = celý den v Excelu.',
              'Každé vylepšení = hovor na IT nebo programátora. A čekání tři měsíce na něco jednoduchého.',
            ].map((pain, i) => (
              <div key={i} className="audit-pains-item">
                <div className="audit-pains-x">✕</div>
                <div>{pain}</div>
              </div>
            ))}
            <div className="audit-pains-punch">
              Každý den bez propojení je den <span className="audit-gold-under">práce navíc pro lidi</span>,<br/>
              kterou by měl dělat software.
            </div>
          </div>
        </div>
      </section>

      {/* ========== BIG TESTIMONIAL ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="rep-quote-block">
            <div className="rep-quote-text">
              Před SyncroX jsem měl 5 systémů, které spolu nemluvily. Účetní mi reporty posílala zpožděně, platby z PayPalu visely, a manažerský přehled jsem si ručně dával dohromady v Excelu. <strong>Dneska sedí všechno samo</strong> — a já mám firmu v mobilu, ne v hlavě.
            </div>
            <div className="rep-quote-author">
              <div className="rep-quote-avatar">OK</div>
              <div className="rep-quote-meta">
                <div className="rep-quote-name">Ondřej Krejčí</div>
                <div className="rep-quote-role">Majitel, e-shop + menší výroba</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PLATFORM PILLARS ========== */}
      <section className="audit-section" id="pillars">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co platforma nabízí</span>
            <h2>Tři věci. V jedné ceně.</h2>
            <p>Integrace mezi systémy. Manažerské reporty. Plán digitalizace.</p>
          </div>
          <div className="mp-pillars">
            <div className="mp-pillar">
              <div className="mp-pillar-num">01 · INTEGRACE</div>
              <h3>Systémy, které spolu konečně mluví</h3>
              <div className="mp-pillar-desc">
                Propojujeme vaše e-shopy, brány, účetnictví, sklady, rezervace a výrobu. Bez přepisování, bez exportů, bez programátora.
              </div>
              <div className="mp-pillar-list">
                {['20+ standardních integrací', 'Custom integrace na míru', 'Chytrý import výpisů', 'Automatické párování plateb'].map((t, i) => (
                  <div key={i} className="mp-pillar-list-item">
                    <span className="mp-pillar-check"><IconCheck size={11} /></span>
                    {t}
                  </div>
                ))}
              </div>
              <a href="/digitalizacni-audit" className="mp-pillar-cta">Zjistit možnosti <IconArrow size={14} /></a>
            </div>
            <div className="mp-pillar">
              <div className="mp-pillar-num">02 · REPORTY</div>
              <h3>Vaše firma na mobilu, v reálném čase</h3>
              <div className="mp-pillar-desc">
                Manažerské reporty na míru, napojené přímo na vaše data. Mobilní aplikace v ceně — vidíte firmu kdekoli, kdykoli.
              </div>
              <div className="mp-pillar-list">
                {['5 klíčových čísel na mobilu', 'iOS + Android aplikace', 'Push alerty na důležité události', 'Úpravy reportů průběžně v ceně'].map((t, i) => (
                  <div key={i} className="mp-pillar-list-item">
                    <span className="mp-pillar-check"><IconCheck size={11} /></span>
                    {t}
                  </div>
                ))}
              </div>
              <a href="/manazerske-reporty" className="mp-pillar-cta">Prohlédnout reporty <IconArrow size={14} /></a>
            </div>
            <div className="mp-pillar">
              <div className="mp-pillar-num">03 · AUDIT</div>
              <h3>Plán, co a jak u vás zdigitalizovat</h3>
              <div className="mp-pillar-desc">
                Začínáme 60minutovým auditem. Poctivě řekneme, co u vás jde a co ne, a jak konkrétně to u vás bude vypadat.
              </div>
              <div className="mp-pillar-list">
                {['60 minut s expertem', 'PDF plán s konkrétními čísly', 'Vyčíslené úspory v Kč', 'Zdarma, bez závazku'].map((t, i) => (
                  <div key={i} className="mp-pillar-list-item">
                    <span className="mp-pillar-check"><IconCheck size={11} /></span>
                    {t}
                  </div>
                ))}
              </div>
              <a href="/digitalizacni-audit" className="mp-pillar-cta">Chci audit zdarma <IconArrow size={14} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== INDUSTRIES ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Pro koho to funguje</span>
            <h2>Vyberte si, kde se vidíte</h2>
            <p>Každý obor řešíme po svém — ne šablonou.</p>
          </div>
          <div className="mp-industries">
            {[
              {
                cls: '',
                label: 'E-SHOPY & RETAIL',
                title: 'Objednávka → platba → faktura → účto',
                items: [
                  'Propojení Shoptet, WooCommerce, Shopify, Upgates',
                  'Párování plateb z GoPay, Stripe, PayPal',
                  'Sklady napříč kanály, marketplace',
                ],
              },
              {
                cls: 'mp-ind-orange',
                label: 'HOTELY & REZERVACE',
                title: 'Rezervace → zálohy → doplatky automaticky',
                items: [
                  'Mews, Previo, Reenio a další',
                  'Cash flow a obsazenost v mobilu',
                  'Propojení s účetnictvím',
                ],
              },
              {
                cls: 'mp-ind-dark',
                label: 'VÝROBA',
                title: 'Výroba, sklad, fakturace v jednom',
                items: [
                  'Data ze skladů a evidence',
                  'Pohledávky a alerty',
                  'Střediska a zakázková evidence',
                ],
              },
              {
                cls: 'mp-ind-violet',
                label: 'ÚČETNÍ FIRMY',
                title: 'Méně práce, více klientů',
                items: [
                  'Automatický import pro klienty',
                  'White-label reporty pro klienty',
                  'Marže, kterou jste neměli',
                ],
              },
            ].map((ind, i) => (
              <div key={i} className="mp-industry">
                <div className={`mp-industry-top ${ind.cls}`}>
                  <div className="mp-industry-label">{ind.label}</div>
                  <div className="mp-industry-title">{ind.title}</div>
                </div>
                <div className="mp-industry-body">
                  {ind.items.map((it, j) => (
                    <div key={j} className="mp-industry-item">{it}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 3 MINI TESTIMONIALS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co říkají klienti</span>
            <h2>Před a po — slovy majitelů</h2>
          </div>
          <div className="rep-quotes3">
            {[
              { stars: 5, text: 'Před rokem jsem manželce odpovídal <strong>„myslím, že dobře"</strong>. Dneska jí řeknu přesně kolik jsme vydělali a kdo nám dluží.', name: 'Jan Novák', role: 'Majitel, e-shop s doplňky stravy', avatar: 'JN' },
              { stars: 5, text: 'Nejvíc mě překvapilo, kolik se toho dá automatizovat. <strong>Účetní si teď dělá analýzy</strong>, ne přepisování.', name: 'Petra Sýkorová', role: 'Jednatelka, rodinný hotel 3*', avatar: 'PS' },
              { stars: 5, text: 'Implementace byla <strong>7 dní od podpisu</strong>. Měsíc jsme dolaďovali detaily. Teď už si nedovedu představit firmu bez toho.', name: 'Martin Kovář', role: 'CEO, výrobní firma', avatar: 'MK' },
            ].map((q, i) => (
              <div key={i} className="rep-quote-mini">
                <div className="rep-quote-mini-stars">{'★'.repeat(q.stars)}</div>
                <div className="rep-quote-mini-text" dangerouslySetInnerHTML={{ __html: q.text }} />
                <div className="rep-quote-mini-author">
                  <div className="rep-quote-mini-avatar">{q.avatar}</div>
                  <div className="rep-quote-mini-meta">
                    <div className="rep-quote-mini-name">{q.name}</div>
                    <div className="rep-quote-mini-role">{q.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CAPABILITIES ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Co platforma umí</span>
            <h2>Není to jen import výpisů</h2>
            <p>Každá schopnost je konfigurovatelná na vaše konkrétní procesy.</p>
          </div>
          <div className="mp-caps">
            {[
              { n: '01', t: 'Chytrý import výpisů', d: 'Přes API nebo z exportu. Plus párování, transformace, custom pravidla — nastavitelná vámi, bez programátora.' },
              { n: '02', t: 'Automatické párování plateb', d: 'GoPay, Stripe, PayPal, Comgate i Shoptet Pay. Poplatky, kurz, peníze na cestě — správně zaúčtované.' },
              { n: '03', t: 'Manažerské reporty', d: 'Na míru vaším procesům. Pět čísel, která řeknou všechno — cash, obrat, pohledávky, prodej, alerty.' },
              { n: '04', t: 'Mobilní aplikace', d: 'iOS + Android. Vaše firma v kapse. Push notifikace na důležité události. Nativní aplikace, ne webová.' },
              { n: '05', t: 'Rozúčtování v podvojném', d: 'Celý účetní případ od faktury přes platbu, poplatek, kurzový rozdíl. Podle vašich pravidel.' },
              { n: '06', t: 'Custom integrace', d: 'Pokud váš systém nemáme, doděláme ho. Zatím ještě žádný neodolal.' },
            ].map((c, i) => (
              <div key={i} className="mp-cap">
                <div className="mp-cap-badge">{c.n}</div>
                <h4>{c.t}</h4>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== INTEGRATIONS ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">20+ integrací v ceně</span>
            <h2>Pohoda, Money, Shoptet a další</h2>
            <p>Integrace nepočítáme po kusech. Vše je zahrnuto v tarifu.</p>
          </div>
          <div className="rep-integrations">
            <div className="rep-int-grid">
              {integrationLogos.map((l, i) => (
                <div key={i} className="rep-int-tile" title={l.name}>
                  <img src={l.src} alt={l.name} loading="lazy" />
                </div>
              ))}
            </div>
            <div className="rep-int-more">
              Nevidíte váš systém? <strong>Doděláme ho.</strong> Každý datový zdroj je nějak připojitelný.
            </div>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section className="audit-section audit-section-alt" id="how">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Jak to probíhá</span>
            <h2>Od auditu do provozu za 2–3 týdny</h2>
          </div>
          <div className="audit-steps">
            <div className="audit-step">
              <div className="audit-step-num">KROK 01</div>
              <div className="audit-step-time">60 min</div>
              <h3>Audit zdarma</h3>
              <p>60minutová konzultace + PDF plán. Poctivě řekneme, co u vás jde a co ne, a kolik vám to reálně dá v Kč.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 02</div>
              <div className="audit-step-time">5–10 dní</div>
              <h3>Nasazení</h3>
              <p>Propojíme vaše systémy. Nastavíme reporty a pravidla přímo na vaše procesy. Vy děláte své — my řešíme za vás.</p>
            </div>
            <div className="audit-step">
              <div className="audit-step-num">KROK 03</div>
              <div className="audit-step-time">průběžně</div>
              <h3>Provoz</h3>
              <p>Aplikace funguje, reporty běží, data tečou. Průběžně upravujeme, když potřebujete něco nového — bez příplatku.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CASE STUDIES ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Reálné výsledky</span>
            <h2>Konkrétní firmy.<br/>Konkrétní čísla.</h2>
          </div>
          <div className="audit-cases">
            {[
              {
                tag: 'E-shop s doplňky stravy',
                img: '/cases/case-eshop.jpg',
                alt: 'E-shop sklad',
                before: '55 hod/měs ručního přepisování',
                after: '3 hod/měs jen kontrola',
                result: '~280 000 Kč úspora ročně',
              },
              {
                tag: 'Rodinný hotel, 3*',
                img: '/cases/case-hotel.jpg',
                alt: 'Rodinný hotel',
                before: 'Rozdíly mezi pokladnou a účtem',
                after: 'Peníze sedí, sezóna v mobilu',
                result: 'Sezónní obrat ↑ 18 % díky datům',
              },
              {
                tag: 'Výrobní firma, 15 lidí',
                img: '/cases/case-vyroba.jpg',
                alt: 'Výrobní firma',
                before: '2 plné úvazky na přepisování',
                after: '0,5 úvazku — zbytek analytika',
                result: '1,5 člověka na produktivní práci',
              },
            ].map((c, i) => (
              <div key={i} className="audit-case">
                <div className="audit-case-img">
                  <img src={c.img} alt={c.alt} loading="lazy" />
                </div>
                <div className="audit-case-head">{c.tag}</div>
                <div className="audit-case-body">
                  <div className="audit-case-row audit-case-row-before">
                    <div className="audit-case-row-label">Dřív</div>
                    <div className="audit-case-row-text">{c.before}</div>
                  </div>
                  <div className="audit-case-row audit-case-row-after">
                    <div className="audit-case-row-label">Dnes</div>
                    <div className="audit-case-row-text">{c.after}</div>
                  </div>
                  <div className="audit-case-result">
                    <div className="audit-case-result-l">Výsledek</div>
                    <div className="audit-case-result-v">{c.result}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== REVIEWS STATS ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="rep-reviews">
            <div className="rep-reviews-big">
              <div className="rep-reviews-stars">★★★★★</div>
              <div className="rep-reviews-score">4,9<small>/ 5</small></div>
              <div className="rep-reviews-count">Průměrné hodnocení od 50+ klientů</div>
            </div>
            <div className="rep-reviews-right">
              <h3>96 % klientů nás doporučuje dál</h3>
              <p>Nejčastější důvod: <strong>„konečně vím, co se v naší firmě děje"</strong>. Další nejčastější: <strong>„lidská cena za to, co dostáváme"</strong>. A třetí: <strong>„když chceme něco změnit, prostě řeknete a oni to udělají"</strong>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== GUARANTEE ========== */}
      <section className="audit-section">
        <div className="audit-container">
          <div className="audit-guarantee">
            <IconShield size={48} />
            <h3>Férová nabídka — bez háčků</h3>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', margin: '12px auto 20px', maxWidth: 560, lineHeight: 1.55 }}>
              Chceme, aby vám to dávalo smysl. Proto máme pravidla takhle:
            </p>
            <div className="audit-guarantee-list">
              {[
                'Audit a ukázka jsou vždy zdarma, bez závazku.',
                'Jeden měsíční tarif zahrnuje vše — integrace, reporty, aplikaci, podporu.',
                'Bez výpovědních dob. Měsíc po měsíci. Nefunguje? Vypnete.',
                'Úpravy reportů i integrací průběžně — bez příplatků.',
                'Pokud váš systém nemáme, doděláme ho — nepřiplatíte.',
              ].map((item, i) => (
                <div key={i} className="audit-guarantee-item">
                  <IconCheck size={20} />
                  <div>{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="audit-section audit-section-alt">
        <div className="audit-container">
          <div className="audit-section-header">
            <span className="audit-eyebrow">Časté otázky</span>
            <h2>Na co se ptají nejčastěji</h2>
          </div>
          <Faq />
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="audit-cta-section" id="form">
        <div className="audit-container">
          <div className="audit-cta-inner">
            <div className="audit-cta-left">
              <h2>Podívejte se, <span className="audit-gold">jak to vypadá u vás</span>.</h2>
              <p>30 minut online. Zdarma. Ukážeme, jak bychom propojili vaše systémy a jak by vypadala vaše firma v mobilní aplikaci.</p>
              <div className="audit-cta-mail">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="22 6 12 13 2 6" />
                </svg>
                obchod@syncrox.cz
              </div>
              <div className="audit-cta-note">Odpovídáme do 24 hodin. Obvykle dřív.</div>

              {/* Mini quote near form */}
              <div style={{
                marginTop: 28,
                padding: '20px 22px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 16,
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 14,
                lineHeight: 1.55,
                maxWidth: 440,
              }}>
                <div style={{ color: '#FDBA74', letterSpacing: '-1px', fontSize: 14, marginBottom: 6 }}>★★★★★</div>
                „<strong style={{ color: '#FFFFFF' }}>Za 30 minut jsme věděli, co přesně potřebujeme.</strong> Bez PowerPoint prezentace, bez prodejních kliček."
                <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255, 255, 255, 0.6)' }}>— Tomáš Vlk, jednatel účetní firmy</div>
              </div>
            </div>
            <MainForm />
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="audit-footer">
        <div className="audit-container">
          <div className="audit-footer-inner">
            <div>© 2026 SyncroX — Platforma pro digitalizaci firem</div>
            <div>
              <a href="/digitalizacni-audit">Digitalizační audit</a>
              <a href="/manazerske-reporty">Manažerské reporty</a>
              <a href="mailto:obchod@syncrox.cz">Kontakt</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ========== EXIT INTENT POPUP ========== */}
      <ExitIntent />
    </div>
  );
}
